import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';

export interface MobileRouteTransition {
  from: string;
  to: string;
  direction: 'forward' | 'back' | 'replace';
  timestamp: number;
}

export const useMobileRouter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [routeHistory, setRouteHistory] = useState<string[]>([]);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Track route history for mobile back navigation
  useEffect(() => {
    setRouteHistory(prev => {
      const newHistory = [...prev];
      if (newHistory[newHistory.length - 1] !== location.pathname) {
        newHistory.push(location.pathname);
        // Keep only last 10 routes to prevent memory issues
        return newHistory.slice(-10);
      }
      return prev;
    });
  }, [location.pathname]);

  const navigateWithTransition = useCallback((
    to: string, 
    options?: { 
      replace?: boolean; 
      state?: any; 
      showToast?: boolean;
      toastMessage?: string;
    }
  ) => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    
    // Add smooth transition effect
    document.body.style.transition = 'opacity 0.2s ease-in-out';
    document.body.style.opacity = '0.9';
    
    setTimeout(() => {
      if (options?.replace) {
        navigate(to, { replace: true, state: options.state });
      } else {
        navigate(to, { state: options.state });
      }
      
      // Show navigation toast if requested
      if (options?.showToast && options?.toastMessage) {
        toast({
          title: "Navigation",
          description: options.toastMessage,
          duration: 2000
        });
      }
      
      // Reset transition
      setTimeout(() => {
        document.body.style.opacity = '1';
        setIsTransitioning(false);
        
        // Clean up styles after transition
        setTimeout(() => {
          document.body.style.transition = '';
        }, 200);
      }, 100);
    }, 150);
  }, [navigate, toast, isTransitioning]);

  const goBack = useCallback(() => {
    if (routeHistory.length > 1) {
      const previousRoute = routeHistory[routeHistory.length - 2];
      setRouteHistory(prev => prev.slice(0, -1));
      navigateWithTransition(previousRoute, { replace: true });
    } else {
      // Fallback to dashboard if no history
      navigateWithTransition('/dashboard', { replace: true });
    }
  }, [routeHistory, navigateWithTransition]);

  const canGoBack = routeHistory.length > 1;

  // Handle device back button on mobile
  useEffect(() => {
    const handlePopState = () => {
      if (routeHistory.length > 1) {
        setRouteHistory(prev => prev.slice(0, -1));
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [routeHistory]);

  // Preload routes for better performance
  const preloadRoute = useCallback((route: string) => {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = route;
    document.head.appendChild(link);
    
    // Clean up after 5 seconds
    setTimeout(() => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    }, 5000);
  }, []);

  return {
    navigateWithTransition,
    goBack,
    canGoBack,
    routeHistory: routeHistory.slice(-5), // Only return last 5 for display
    isTransitioning,
    currentRoute: location.pathname,
    preloadRoute
  };
};
